'use client';

/* eslint-disable react/jsx-props-no-spreading */
import { c as _c } from "react/compiler-runtime";
import { DEFAULT_COUNTRY } from '@onefootprint/core';
import type { Iso3166TwoDigitCountryCode } from '@onefootprint/request-types';
import { useCombobox } from 'downshift';
import noop from 'lodash/noop';
import take from 'lodash/take';
import type React from 'react';
import { forwardRef, useEffect, useRef } from 'react';
import mergeRefs from 'react-merge-refs';
import usePlacesAutocomplete from 'use-places-autocomplete';
import type { InputProps } from '../internal/input';
import Input from '../internal/input';
import type { AddressPrediction } from './address-input.types';
import AddressDropdownFooter from './components/address-dropdown-footer';
import AddressDropdownItem from './components/address-dropdown-item';
import usePopper from './hooks/use-popper';
const MAX_OF_RESULTS = 5;
export type AddressInputProps = Omit<InputProps, 'onSelect'> & {
  onSelect?: (prediction?: AddressPrediction | null) => void;
  country?: Iso3166TwoDigitCountryCode;
};
const AddressInput = forwardRef<HTMLInputElement, AddressInputProps>((t0, ref) => {
  const $ = _c(26);
  let onChangeText;
  let props;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let value;
  if ($[0] !== t0) {
    ({
      country: t1,
      onBlur: t2,
      onChange: t3,
      onChangeText,
      onKeyDown: t4,
      onSelect: t5,
      value,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = onChangeText;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = value;
  } else {
    onChangeText = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
    value = $[8];
  }
  const country = t1 === undefined ? DEFAULT_COUNTRY.value : t1;
  const onBlur = t2 === undefined ? noop : t2;
  const onChange = t3 === undefined ? noop : t3;
  const onKeyDown = t4 === undefined ? noop : t4;
  const onSelect = t5 === undefined ? noop : t5;
  const isUncontrolled = value === undefined;
  const localRef = useRef(null);
  const {
    setReferenceElement,
    setPopperElement,
    popper
  } = usePopper();
  let t6;
  if ($[9] !== country) {
    t6 = {
      requestOptions: {
        componentRestrictions: {
          country
        }
      }
    };
    $[9] = country;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const {
    suggestions: t7,
    setValue,
    clearCache,
    clearSuggestions
  } = usePlacesAutocomplete(t6);
  const {
    data
  } = t7;
  let t8;
  if ($[11] !== clearCache || $[12] !== clearSuggestions) {
    t8 = () => {
      clearCache();
      clearSuggestions();
    };
    $[11] = clearCache;
    $[12] = clearSuggestions;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== clearCache || $[15] !== clearSuggestions || $[16] !== country) {
    t9 = [country, clearCache, clearSuggestions];
    $[14] = clearCache;
    $[15] = clearSuggestions;
    $[16] = country;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  useEffect(t8, t9);
  const options = take(data, MAX_OF_RESULTS);
  let t11;
  if ($[18] !== onChangeText || $[19] !== setValue) {
    t11 = t12 => {
      const {
        inputValue: t13
      } = t12;
      const nextInputValue = t13 === undefined ? "" : t13;
      onChangeText?.(nextInputValue);
      setValue(nextInputValue);
    };
    $[18] = onChangeText;
    $[19] = setValue;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const {
    getComboboxProps,
    getInputProps,
    getItemProps,
    getMenuProps,
    getToggleButtonProps,
    highlightedIndex,
    isOpen: downShiftIsOpen
  } = useCombobox({
    items: options,
    itemToString: _temp,
    onSelectedItemChange: t10 => {
      const {
        selectedItem
      } = t10;
      onChangeText?.(selectedItem?.structured_formatting.main_text || "");
      onSelect(selectedItem);
      const hasToTriggerChangeEvent = isUncontrolled && localRef.current;
      if (hasToTriggerChangeEvent && localRef.current) {
        const event = new window.Event("change", {
          bubbles: true
        });
        localRef.current.dispatchEvent(event);
      }
    },
    onInputValueChange: t11
  });
  let t12;
  if ($[21] !== getComboboxProps) {
    t12 = getComboboxProps();
    $[21] = getComboboxProps;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const comboBoxProps = t12;
  const toggleButtonProps = getToggleButtonProps();
  const inputProps = getInputProps();
  const menuProps = getMenuProps({}, {
    suppressRefError: true
  });
  const hasResults = options.length > 0;
  const isDropdownOpen = downShiftIsOpen && hasResults;
  const handleChange = event_0 => {
    inputProps.onChange(event_0);
    onChange(event_0);
  };
  const handleBlur = event_1 => {
    inputProps.onBlur(event_1);
    onBlur(event_1);
  };
  const handleKeyDown = event_2 => {
    inputProps.onKeyDown(event_2);
    onKeyDown(event_2);
  };
  let t13;
  if ($[23] !== getItemProps || $[24] !== highlightedIndex) {
    t13 = (item_0, index) => {
      const itemProps = getItemProps({
        item: item_0,
        index
      });
      return <AddressDropdownItem data-dd-privacy="mask" disableHoverStyles={highlightedIndex !== -1} highlighted={highlightedIndex === index} key={item_0.place_id} subtitle={item_0.structured_formatting.secondary_text} title={item_0.structured_formatting.main_text} {...itemProps} />;
    };
    $[23] = getItemProps;
    $[24] = highlightedIndex;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const renderAddressItem = t13;
  return <div {...comboBoxProps}><div ref={setReferenceElement} className="relative"><Input {...props} {...toggleButtonProps} aria-autocomplete={inputProps["aria-autocomplete"]} aria-controls={inputProps["aria-controls"]} autoComplete="address-line1" id={props.id || inputProps.id} onBlur={handleBlur} onChange={handleChange} onKeyDown={handleKeyDown} tabIndex={0} value={value} data-dd-privacy="mask" ref={mergeRefs([localRef, inputProps.ref, toggleButtonProps.ref, ref])} />{isDropdownOpen ? <ul className="fp-dropdown-menu max-h-[332px] w-full outline-none pt-2" {...menuProps} {...popper.attributes.popper} ref={mergeRefs([menuProps.ref, setPopperElement])} style={popper.styles.popper} data-dd-privacy="mask">{options.map(renderAddressItem)}<AddressDropdownFooter /></ul> : null}</div></div>;
});
export default AddressInput;
function _temp(item) {
  return item ? item.structured_formatting.main_text : "";
}